import { useCallback, useEffect, useRef, useState } from 'react';
import { syndicationApi } from './syndication-api';
import { useSyndicationFilters } from './use-syndication-filters';
import { getUpdatedPropertySyndication } from './utils';
import { DEFAULT_QUERY } from './constants';

export const useSyndication = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [drawerMode, setDrawerMode] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [propertiesSyndicationByPage, setPropertiesSyndicationByPage] =
    useState({});
  const [
    syndicationTableSelectedProperties,
    setSyndicationTableSelectedProperties,
  ] = useState([]);

  const activeChannelsRef = useRef();
  const totalPropertyCountRef = useRef(0);

  const {
    userFilteredChannels,
    filteredPropertyIdsRef,
    setUserFilteredChannels,
  } = useSyndicationFilters();

  const shouldFetchInitialChannelData =
    Object.keys(propertiesSyndicationByPage).length === 0;

  const fetchSyndication = useCallback(
    async (query) => {
      setIsLoading(true);

      const queryPage = query.page;

      const filteredPropertyIds = filteredPropertyIdsRef.current;
      const hasFilteredPropertyIds = filteredPropertyIds.length > 0;

      const apiQueryParams = {
        ...query,
        ...(hasFilteredPropertyIds && {
          propertyIds: filteredPropertyIds,
        }),
      };

      try {
        const {
          activeChannels = [],
          propertiesSyndication = [],
          totalPropertyCount = 0,
        } = await syndicationApi.getPaginatedSyndicationSubscriptions(
          apiQueryParams
        );

        query.isTableReloadRequired
          ? setPropertiesSyndicationByPage({
              [queryPage]: propertiesSyndication,
            })
          : setPropertiesSyndicationByPage((prevState) => {
              return {
                ...prevState,
                [queryPage]: propertiesSyndication,
              };
            });

        activeChannelsRef.current = activeChannels;
        totalPropertyCountRef.current = totalPropertyCount;

        setCurrentPage(queryPage);
      } catch (e) {
        setErrorMessage(e?.message);
      }

      setIsLoading(false);
    },
    [filteredPropertyIdsRef]
  );

  const getSyndication = useCallback(
    async (query = DEFAULT_QUERY) => {
      setErrorMessage('');
      const { isTableReloadRequired, page } = query;

      const shouldFetchData =
        isTableReloadRequired || !propertiesSyndicationByPage[page];

      shouldFetchData ? fetchSyndication(query) : setCurrentPage(page);
    },
    [fetchSyndication, propertiesSyndicationByPage]
  );

  useEffect(() => {
    if (shouldFetchInitialChannelData) {
      getSyndication();
    }
  }, [getSyndication, shouldFetchInitialChannelData]);

  const handleDrawerMode = (mode) => () => {
    setDrawerMode(mode);
  };

  const updatePropertySyndication = (propertyId, channelUpdates) => {
    setPropertiesSyndicationByPage((previousState) => {
      const pageData = previousState[currentPage];
      const updatedPageData = pageData.map((propertySyndication) => {
        const { property } = propertySyndication;
        if (propertyId === property.id) {
          const updatedPropertySyndication = getUpdatedPropertySyndication(
            propertySyndication,
            channelUpdates
          );
          return updatedPropertySyndication;
        }
        return propertySyndication;
      });
      return {
        ...previousState,
        [currentPage]: updatedPageData,
      };
    });
    //TODO handle api call to update the database
  };

  const filteredChannels =
    userFilteredChannels.length > 0
      ? userFilteredChannels
      : activeChannelsRef.current;

  const isActivateChannelsVisible =
    filteredChannels && filteredChannels.length === 0;

  const propertiesSyndication = propertiesSyndicationByPage[currentPage] || [];

  return {
    drawerMode,
    errorMessage,
    filteredChannels: filteredChannels || [],
    filteredPropertyIdsRef,
    getSyndication,
    handleDrawerMode,
    isActivateChannelsVisible,
    isLoading,
    propertiesSyndication,
    setSyndicationTableSelectedProperties,
    setUserFilteredChannels,
    syndicationTableSelectedProperties,
    totalPropertyCount: totalPropertyCountRef.current,
    updatePropertySyndication,
  };
};
